import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ordersAPI, returnsAPI, paymentGatewaysAPI } from '../lib/api';
import { toast } from 'sonner';
import { Package, Truck, CheckCircle, Clock, ChevronLeft, MapPin, Phone, FileText, X, RefreshCw, Upload, CreditCard } from 'lucide-react';

const statusSteps = [
  { key: 'pending_payment', label: 'Payment Pending', icon: CreditCard },
  { key: 'pending', label: 'Order Placed', icon: Clock },
  { key: 'processing', label: 'Processing', icon: Package },
  { key: 'shipped', label: 'Shipped', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle },
];

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [returnData, setReturnData] = useState({
    returnType: '',
    reason: '',
    refundMethod: 'original'
  });
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [cancellationEligibility, setCancellationEligibility] = useState(null);
  const [returnEligibility, setReturnEligibility] = useState(null);
  const [showTrackingDialog, setShowTrackingDialog] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [loadingTracking, setLoadingTracking] = useState(false);
  const [repaymentLoading, setRepaymentLoading] = useState(false);

  const fetchOrder = useCallback(async () => {
    try {
      const response = await ordersAPI.getOne(id);
      setOrder(response.data);
    } catch (error) {
      toast.error('Order not found');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const checkCancellationEligibility = async () => {
    try {
      const response = await ordersAPI.checkCancellationEligibility(id);
      setCancellationEligibility(response.data);
    } catch (error) {
      console.error('Failed to check cancellation eligibility:', error);
    }
  };

  const checkReturnEligibility = async () => {
    try {
      const response = await ordersAPI.checkReturnEligibility(id);
      setReturnEligibility(response.data);
    } catch (error) {
      console.error('Failed to check return eligibility:', error);
    }
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }

    try {
      const response = await ordersAPI.cancelOrder(id, {
        order_id: id,
        reason: cancelReason,
        cancellation_type: 'customer'
      });

      toast.success(response.data.message);
      setShowCancelDialog(false);
      fetchOrder(); // Refresh order data
    } catch (error) {
      toast.error('Failed to cancel order');
    }
  };

  const handleCreateReturn = async () => {
    if (!returnData.returnType || !returnData.reason.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const returnPayload = {
        order_id: id,
        items: order.items, // Return all items for now
        reason: returnData.reason,
        return_type: returnData.returnType,
        refund_method: returnData.refundMethod,
        description: returnData.reason
      };

      const response = await ordersAPI.createReturn(id, returnPayload);

      // Upload evidence files if any
      if (evidenceFiles.length > 0) {
        const formData = new FormData();
        evidenceFiles.forEach(file => {
          formData.append('files', file);
        });

        try {
          await returnsAPI.uploadEvidence(response.data.return_id, formData);
          toast.success('Return request submitted with evidence');
        } catch (evidenceError) {
          toast.success('Return request submitted (evidence upload failed)');
        }
      } else {
        toast.success(response.data.message);
      }

      setShowReturnDialog(false);
      setReturnData({ returnType: '', reason: '', refundMethod: 'original' });
      setEvidenceFiles([]);
    } catch (error) {
      toast.error('Failed to create return request');
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 5) {
      toast.error('Maximum 5 files allowed');
      return;
    }
    setEvidenceFiles(files);
  };

  const handleDownloadInvoice = async () => {
    try {
      const response = await ordersAPI.getInvoice(id);

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${order.order_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleRepayment = async () => {
    if (!order) return;
    setRepaymentLoading(true);

    try {
      toast.loading('Initiating payment...');
      // Re-initiate payment using the original payment method (or default to razorpay/online)
      // Since we don't store "razorpay" vs "phonepe" specifically on order.payment_method sometimes (it might just be 'online' or specific)
      // We will try to use the stored payment method from the order if possible, or fallback.
      const methodToUse = order.payment_method === 'cod' ? 'razorpay' : order.payment_method;

      const paymentRes = await paymentGatewaysAPI.initiate({
        order_id: order.id,
        gateway_id: methodToUse
      });

      const payData = paymentRes.data;

      // Razorpay Flow
      if (payData.provider === 'razorpay') {
        const res = await loadRazorpayScript();
        if (!res) {
          toast.error('Razorpay SDK failed to load. Are you online?');
          toast.dismiss();
          setRepaymentLoading(false);
          return;
        }

        const options = {
          key: payData.key_id,
          amount: payData.amount,
          currency: payData.currency,
          name: payData.name,
          description: payData.description,
          order_id: payData.order_id,
          prefill: payData.prefill,
          theme: {
            color: "#3399cc"
          },
          handler: async function (responseRp) {
            toast.loading("Verifying Payment...");
            try {
              await paymentGatewaysAPI.verifyRazorpay({
                razorpay_payment_id: responseRp.razorpay_payment_id,
                razorpay_order_id: responseRp.razorpay_order_id,
                razorpay_signature: responseRp.razorpay_signature,
                internal_order_id: order.id
              });
              toast.dismiss();
              toast.success("Payment Successful!");
              fetchOrder(); // Refresh to see updated status
            } catch (err) {
              console.error(err);
              toast.dismiss();
              toast.error("Payment Verification Failed");
            }
          },
          modal: {
            ondismiss: function () {
              toast.dismiss();
              toast.info("Payment Cancelled");
            }
          }
        };

        const paymentObject = new window.Razorpay(options);
        paymentObject.open();
        toast.dismiss();
        setRepaymentLoading(false);
        return;
      }

      // Legacy URL Flow
      if (payData.payment_url) {
        if (payData.payment_url.includes('mock-success')) {
          setTimeout(async () => {
            await paymentGatewaysAPI.confirmMock({ order_id: order.id });
            toast.dismiss();
            toast.success('Payment Successful!');
            fetchOrder();
          }, 1000);
        } else {
          window.location.href = payData.payment_url;
        }
      }

    } catch (error) {
      console.error("Repayment failed", error);
      toast.dismiss();
      toast.error("Failed to initiate payment. Please try again.");
    } finally {
      setRepaymentLoading(false);
    }
  };

  const fetchTracking = async () => {
    if (!order.tracking_number) {
      toast.error('No tracking number available');
      return;
    }

    setLoadingTracking(true);
    try {
      const response = await ordersAPI.getTracking(id);
      setTrackingData(response.data);
      setShowTrackingDialog(true);
    } catch (error) {
      toast.error('Failed to fetch tracking information');
    } finally {
      setLoadingTracking(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-40 bg-muted rounded" />
          <div className="h-60 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (!order) return null;

  // Determine current step index
  // If pending_payment, index is 0. If pending (placed), index is 1, etc.
  let currentStepIndex = 0;
  if (order.status === 'pending_payment') {
    currentStepIndex = 0;
  } else {
    // Find index in steps excluding pending_payment which is special handling specific to this logic or just map it
    const foundIndex = statusSteps.findIndex(s => s.key === order.status);
    if (foundIndex !== -1) {
      currentStepIndex = foundIndex;
    } else {
      // If status not found (e.g. cancelled), handle appropriately
      currentStepIndex = -1;
    }
  }

  // If payment is pending, we might want to ONLY show the first step as active.
  // Actually, let's keep it simple: map order status to the steps.

  const getStatusBadgeClass = (status) => {
    const classes = {
      pending_payment: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      pending: 'status-pending',
      processing: 'status-processing',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
      cancelled: 'status-cancelled',
      returned: 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return classes[status] || 'status-pending';
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="order-detail-page">
      <button
        onClick={() => navigate('/orders')}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Orders
      </button>

      {/* Order Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold">Order #{order.order_number}</h1>
          <p className="text-muted-foreground">
            Placed on {new Date(order.created_at).toLocaleDateString('en-IN', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </p>
        </div>
        <Badge className={`status-badge ${getStatusBadgeClass(order.status)}`}>
          {(order.status === 'pending_payment' ? 'Payment Pending' : order.status).replace('_', ' ').toUpperCase()}
        </Badge>
      </div>

      {/* Order Tracking */}
      {order.status !== 'cancelled' && order.status !== 'returned' && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Order Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between relative px-2">
              {/* Progress Line */}
              <div className="absolute top-5 left-8 right-8 h-1 bg-muted">
                <div
                  className="h-full bg-primary transition-all duration-500 ease-in-out"
                  style={{
                    width: currentStepIndex >= 0
                      ? `${(currentStepIndex / (statusSteps.length - 1)) * 100}%`
                      : '0%'
                  }}
                />
              </div>

              {statusSteps.map((step, index) => {
                const isCompleted = currentStepIndex >= 0 && index <= currentStepIndex;
                const isCurrent = index === currentStepIndex;
                const StepIcon = step.icon;

                // Hide pending_payment step if order is already placed/processing etc?
                // Or show all steps. Let's show all steps.

                return (
                  <div key={step.key} className="relative flex flex-col items-center z-10">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all border-2 
                        ${isCompleted ? 'bg-primary border-primary text-white' : 'bg-background border-muted text-muted-foreground'} 
                        ${isCurrent ? 'ring-4 ring-primary/20' : ''}`}>
                      <StepIcon className="w-5 h-5" />
                    </div>
                    <span className={`mt-2 text-xs font-medium text-center max-w-[80px] ${isCompleted ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>

            {order.status === 'pending_payment' && (
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl flex items-center justify-between">
                <div>
                  <p className="font-semibold text-yellow-800">Payment is Pending</p>
                  <p className="text-sm text-yellow-700">Complete the payment to process your order.</p>
                </div>
                <Button
                  onClick={handleRepayment}
                  disabled={repaymentLoading}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white"
                >
                  {repaymentLoading ? 'Loading...' : 'Complete Payment'}
                </Button>
              </div>
            )}

            {order.tracking_number && (
              <div className="mt-6 p-4 bg-muted/50 rounded-xl">
                <p className="text-sm text-muted-foreground">Tracking Number</p>
                <p className="font-mono font-semibold">{order.tracking_number}</p>
                {order.courier_provider && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Courier: {order.courier_provider}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Return History */}
      {order.notes && order.notes.some(n => n.type === 'pos_return') && (
        <Card className="mb-6 border-orange-200 bg-orange-50/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
              <RefreshCw className="w-5 h-5" />
              Return History
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {order.notes.filter(n => n.type === 'pos_return').map((note, idx) => (
              <div key={idx} className="bg-white rounded-lg p-3 border border-orange-100 shadow-sm">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">
                    Returned on {new Date(note.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
                <div className="space-y-2">
                  {note.items.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="font-medium text-foreground">
                        {item.product_name || `Product #${item.product_id}`}
                        <span className="text-muted-foreground font-normal ml-2">x {item.quantity}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Items */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5" />
              Items ({order.items.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {order.items.map((item, index) => (
              <div key={index} className="flex gap-3">
                <div className="w-12 h-12 bg-muted rounded-lg flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-medium">{item.product_name}</p>
                  <p className="text-sm text-muted-foreground">SKU: {item.sku}</p>
                  <div className="flex justify-between mt-1">
                    <span className="text-sm">Qty: {item.quantity}</span>
                    <span className="font-semibold">₹{item.total.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))}

            <Separator />

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>₹{order.subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">GST (Included)</span>
                <span className="text-slate-500">₹{order.gst_total.toFixed(2)}</span>
              </div>

              {/* Calculate packing from items */}
              {order.items.reduce((acc, item) => acc + (item.packing_charges || 0), 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Packing Charges</span>
                  <span>₹{order.items.reduce((acc, item) => acc + (item.packing_charges || 0), 0).toLocaleString()}</span>
                </div>
              )}

              {/* Infer delivery fee */}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Delivery Charges</span>
                <span className={order.grand_total - order.subtotal - order.items.reduce((acc, i) => acc + (i.packing_charges || 0), 0) + (order.discount_amount || 0) <= 0 ? 'text-emerald-600' : ''}>
                  {/* Simplified check for display: if GrandTotal > Subtotal + Packing, diff is delivery */}
                  {(order.grand_total - order.subtotal - order.items.reduce((acc, i) => acc + (i.packing_charges || 0), 0) + (order.discount_amount || 0)) <= 0.1
                    ? 'FREE'
                    : `₹${Math.round(order.grand_total - order.subtotal - order.items.reduce((acc, i) => acc + (i.packing_charges || 0), 0) + (order.discount_amount || 0))}`
                  }
                </span>
              </div>

              {order.discount_amount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount</span>
                  <span>-₹{order.discount_amount.toLocaleString()}</span>
                </div>
              )}

              <div className="flex justify-between text-lg font-bold pt-2 border-t">
                <span>Total</span>
                <span className="price-tag text-primary">₹{order.grand_total.toLocaleString()}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Delivery & Payment */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Delivery Address
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-medium">{order.shipping_address.name}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {order.shipping_address.line1}
                {order.shipping_address.line2 && `, ${order.shipping_address.line2}`}
              </p>
              <p className="text-sm text-muted-foreground">
                {order.shipping_address.city}, {order.shipping_address.state} - {order.shipping_address.pincode}
              </p>
              {order.shipping_address.phone && (
                <p className="text-sm flex items-center gap-1 mt-2">
                  <Phone className="w-4 h-4" />
                  {order.shipping_address.phone}
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Payment Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Method</span>
                  <span className="capitalize">{order.payment_method}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge variant={order.payment_status === 'paid' ? 'default' : 'secondary'}>
                    {order.payment_status}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <Button variant="outline" className="w-full" onClick={handleDownloadInvoice}>
              <FileText className="w-4 h-4 mr-2" />
              Download Invoice
            </Button>

            {/* Track Order Button */}
            {order.tracking_number && (
              <Button
                variant="outline"
                className="w-full"
                onClick={fetchTracking}
                disabled={loadingTracking}
              >
                <Truck className="w-4 h-4 mr-2" />
                {loadingTracking ? 'Loading...' : 'Track Order'}
              </Button>
            )}

            {/* Cancellation Button */}
            {order.status !== 'delivered' && order.status !== 'cancelled' && order.status !== 'returned' && order.status !== 'pending_payment' && (
              <Button
                variant="outline"
                className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => {
                  checkCancellationEligibility();
                  setShowCancelDialog(true);
                }}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel Order
              </Button>
            )}

            {/* Return Button */}
            {order.status === 'delivered' && (
              <Button
                variant="outline"
                className="w-full text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                onClick={() => {
                  checkReturnEligibility();
                  setShowReturnDialog(true);
                }}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Request Return
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Order Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {cancellationEligibility && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Refund Amount:</strong> ₹{cancellationEligibility.refund_amount?.toLocaleString()}
                </p>
                <p className="text-sm text-blue-800">
                  <strong>Refund Timeline:</strong> {cancellationEligibility.refund_timeline}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="cancelReason">Reason for Cancellation</Label>
              <Select value={cancelReason} onValueChange={setCancelReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Changed my mind">Changed my mind</SelectItem>
                  <SelectItem value="Found better price elsewhere">Found better price elsewhere</SelectItem>
                  <SelectItem value="No longer needed">No longer needed</SelectItem>
                  <SelectItem value="Ordered by mistake">Ordered by mistake</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
                Keep Order
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancelOrder}
                disabled={!cancelReason}
              >
                Cancel Order
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Return Request Dialog */}
      <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Request Return</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {returnEligibility && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>Return Window:</strong> {returnEligibility.return_window_remaining}
                </p>
                <p className="text-sm text-green-800">
                  <strong>Refund Timeline:</strong> {returnEligibility.refund_timeline}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="returnType">Return Type</Label>
              <Select
                value={returnData.returnType}
                onValueChange={(value) => setReturnData({ ...returnData, returnType: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select return type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="defective">Product is defective/damaged</SelectItem>
                  <SelectItem value="wrong_item">Wrong item received</SelectItem>
                  <SelectItem value="not_satisfied">Not satisfied with product</SelectItem>
                  <SelectItem value="damaged">Package was damaged</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="returnReason">Detailed Reason</Label>
              <Textarea
                id="returnReason"
                placeholder="Please describe the issue in detail..."
                value={returnData.reason}
                onChange={(e) => setReturnData({ ...returnData, reason: e.target.value })}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="refundMethod">Refund Method</Label>
              <Select
                value={returnData.refundMethod}
                onValueChange={(value) => setReturnData({ ...returnData, refundMethod: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="original">Original payment method</SelectItem>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="store_credit">Store credit</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="evidence">Upload Evidence (Optional)</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                <input
                  type="file"
                  id="evidence"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label htmlFor="evidence" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-600">
                    Click to upload photos or videos (Max 5 files)
                  </p>
                </label>
                {evidenceFiles.length > 0 && (
                  <div className="mt-2 text-sm text-green-600">
                    {evidenceFiles.length} file(s) selected
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowReturnDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateReturn}
                disabled={!returnData.returnType || !returnData.reason.trim()}
              >
                Submit Return Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tracking Dialog */}
      <Dialog open={showTrackingDialog} onOpenChange={setShowTrackingDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Order Tracking - {order.order_number}</DialogTitle>
          </DialogHeader>
          {trackingData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">AWB Number</Label>
                  <p className="font-mono font-medium">{trackingData.awb}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Current Status</Label>
                  <Badge className="mt-1">
                    {trackingData.current_status}
                  </Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground">Current Location</Label>
                  <p>{trackingData.current_location || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Expected Delivery</Label>
                  <p>{trackingData.expected_delivery || 'N/A'}</p>
                </div>
              </div>

              {trackingData.tracking_history && trackingData.tracking_history.length > 0 && (
                <div>
                  <Label className="text-muted-foreground mb-2 block">Tracking History</Label>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {trackingData.tracking_history.map((event, index) => (
                      <div key={index} className="flex gap-3 p-3 bg-muted/50 rounded-lg">
                        <Clock className="w-4 h-4 text-muted-foreground mt-0.5" />
                        <div className="flex-1">
                          <p className="font-medium">{event.status}</p>
                          <p className="text-sm text-muted-foreground">{event.location}</p>
                          <p className="text-xs text-muted-foreground">{event.date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
